import { createClient } from '@supabase/supabase-js';
import { Label, ListenData, Release } from '../types';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Supabase URL and Anon Key must be provided in environment variables');
}

// Initialize the Supabase client with additional options
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
});

export const fetchLabels = async (): Promise<Label[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('No authenticated user found in fetchLabels');
      return []; // Return empty array instead of throwing error
    }

    // First get the followed label IDs
    const { data: followedData, error: followError } = await supabase
      .from('user_follow_labels')
      .select('label_id')
      .eq('user_id', user.id);

    if (followError) {
      console.error('Error fetching followed labels:', followError);
      throw followError;
    }
    
    if (!followedData || followedData.length === 0) {
      return []; // Return empty array instead of throwing error
    }

    const followedIds = followedData.map(d => d.label_id);

    // Then get the label details for those IDs
    const { data: labels, error: labelsError } = await supabase
      .from('labels')
      .select('*')
      .in('id', followedIds)
      .order('label_name');

    if (labelsError) {
      console.error('Error fetching label details:', labelsError);
      throw labelsError;
    }
    
    if (!labels) return [];

    // Transform to match the Label interface
    return labels.map(label => ({
      id: label.id,
      label_name: label.label_name,
      label_url: label.label_url,
      tags: label.tags,
      releases: [], // Empty initially
    }));

  } catch (error) {
    console.error('Error fetching labels:', error);
    return []; // Return empty array instead of throwing error
  }
};

export const fetchReleasesForLabels = async (labelIds: number[], listenedReleaseIds: number[]): Promise<Map<number, Release[]>> => {
  try {
    // Skip if no label IDs provided
    if (!labelIds || labelIds.length === 0) {
      return new Map();
    }
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('No authenticated user found in fetchReleasesForLabels');
      return new Map();
    }

    // Get all releases for the specified labels first
    const { data: releases, error: releasesError } = await supabase
      .rpc('get_latest_releases_per_label', { 
        p_label_ids: labelIds,
        p_limit: 5
      });

    if (releasesError) {
      console.error('Error fetching releases:', releasesError);
      throw releasesError;
    }
    if (!releases) return new Map();

    // Create a Set of listened release IDs for quick lookup
    const listenedSet = new Set(listenedReleaseIds);

    // Group releases by label_id (already limited and sorted by the database)
    const releasesMap = new Map<number, Release[]>();
    labelIds.forEach(labelId => {
      const labelReleases = releases
        .filter((release: Release) => (release.label_id || 0) === labelId)
        .map((release: Release) => ({
          id: release.id || 0,
          label_id: release.label_id || 0,
          title: release.title,
          artist: release.artist,
          release_url: release.release_url || '',
          release_date: release.release_date,
          image_url: release.image_url,
          genres: Array.isArray(release.genres) ? release.genres : [],
          rich: Boolean(release.rich),
          listened: release.id ? listenedSet.has(release.id) : false
        }));
      
      releasesMap.set(labelId, labelReleases);
    });

    return releasesMap;
  } catch (error) {
    console.error('Error fetching releases:', error);
    return new Map();
  }
};

/**
 * Updates the user's listened releases in the database
 * 
 * This function compares the current saved releases (provided as currentSavedIds)
 * with the new set of releases the user wants to save. It then performs only the
 * necessary database operations (add/remove) to update the database.
 * 
 * Optimization: Instead of fetching the current state from the database,
 * we use the provided currentSavedIds parameter. This eliminates an unnecessary
 * database read and improves performance.
 * 
 * @param listenData - Array containing the releases to mark as listened
 * @param currentSavedIds - Array of release IDs that are currently saved in the database
 * @returns A promise that resolves to an object with success status and optional error message
 */
export const updateListenData = async (
  listenData: ListenData[],
  currentSavedIds: number[] = []
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('No authenticated user found in updateListenData');
      return { success: false, error: 'No authenticated user' };
    }

    // Use the provided currentSavedIds instead of fetching from database
    const currentSet = new Set(currentSavedIds);
    const newSet = new Set<number>();

    // Add all checked releases from all labels
    listenData.forEach(labelData => {
      labelData.checked_releases.forEach(releaseId => {
        newSet.add(releaseId);
      });
    });

    // Determine which records to add and remove
    const toAdd = Array.from(newSet).filter(id => !currentSet.has(id));
    const toRemove = Array.from(currentSet).filter(id => !newSet.has(id));

    // Skip if no changes
    if (toAdd.length === 0 && toRemove.length === 0) {
      return { success: true };
    }

    // Remove old records
    if (toRemove.length > 0) {
      const { error: removeError } = await supabase
        .from('user_listened_releases')
        .delete()
        .eq('user_id', user.id)
        .in('release_id', toRemove);

      if (removeError) {
        console.error('Error removing records:', removeError);
        throw removeError;
      }
    }

    // Add new records
    if (toAdd.length > 0) {
      const { error: addError } = await supabase
        .from('user_listened_releases')
        .insert(
          toAdd.map(releaseId => ({
            user_id: user.id,
            release_id: releaseId,
          }))
        );

      if (addError) {
        console.error('Error adding records:', addError);
        throw addError;
      }
    }

    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    console.error('Error updating listen data:', error);
    return { success: false, error: errorMessage };
  }
};

// New function to fetch followed labels using the database function
export const fetchFollowedLabels = async (
  page: number = 1, 
  itemsPerPage: number = 20, 
  letterFilter: string | null = null,
  tagFilter: string | null = null,
  searchTerm: string | null = null
): Promise<{ data: any[], count: number }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('No authenticated user found in fetchFollowedLabels');
      return { data: [], count: 0 };
    }

    // Fetch labels using the database function
    const { data, error } = await supabase
      .rpc('get_followed_labels', { 
        user_id_param: user.id,
        page_number: page,
        items_per_page: itemsPerPage,
        letter_filter: letterFilter,
        tag_filter: tagFilter,
        search_term: searchTerm
      });

    if (error) {
      console.error('Error fetching followed labels:', error);
      throw error;
    }
    
    // Get the total count using the count function
    const { data: countData, error: countError } = await supabase
      .rpc('count_followed_labels', {
        user_id_param: user.id,
        letter_filter: letterFilter,
        tag_filter: tagFilter,
        search_term: searchTerm
      });
      
    if (countError) {
      console.error('Error counting followed labels:', countError);
      throw countError;
    }
    
    return { 
      data: data || [],
      count: countData || 0
    };
  } catch (error) {
    console.error('Error fetching followed labels:', error);
    return { data: [], count: 0 };
  }
};

// New function to fetch unfollowed labels using the database function
export const fetchUnfollowedLabels = async (
  page: number = 1, 
  itemsPerPage: number = 20, 
  letterFilter: string | null = null,
  tagFilter: string | null = null,
  searchTerm: string | null = null
): Promise<{ data: any[], count: number }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('No authenticated user found in fetchUnfollowedLabels');
      return { data: [], count: 0 };
    }

    // Fetch labels using the database function
    const { data, error } = await supabase
      .rpc('get_unfollowed_labels', { 
        user_id_param: user.id,
        page_number: page,
        items_per_page: itemsPerPage,
        letter_filter: letterFilter,
        tag_filter: tagFilter,
        search_term: searchTerm
      });

    if (error) {
      console.error('Error fetching unfollowed labels:', error);
      throw error;
    }
    
    // Get the total count using the count function
    const { data: countData, error: countError } = await supabase
      .rpc('count_unfollowed_labels', {
        user_id_param: user.id,
        letter_filter: letterFilter,
        tag_filter: tagFilter,
        search_term: searchTerm
      });
      
    if (countError) {
      console.error('Error counting unfollowed labels:', countError);
      throw countError;
    }
    
    return { 
      data: data || [],
      count: countData || 0
    };
  } catch (error) {
    console.error('Error fetching unfollowed labels:', error);
    return { data: [], count: 0 };
  }
};

/**
 * Submit a new Bandcamp label URL to be added to the database
 * This function inserts the URL into the labels_to_add table for later processing
 * 
 * @param labelUrl - The URL of the Bandcamp label page
 * @param userId - The ID of the user submitting the label
 * @returns A promise that resolves to an object with success status and optional error message
 */
export const submitLabelUrl = async (
  labelUrl: string,
  userId: string
): Promise<{ success: boolean; error?: string; labelId?: number }> => {
  try {
    // Verify user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('No authenticated user found in submitLabelUrl');
      return { success: false, error: 'No authenticated user' };
    }
    
    // Basic validation - must include bandcamp.com
    if (!labelUrl.includes('bandcamp.com')) {
      return { 
        success: false, 
        error: 'Invalid URL. Please provide a valid Bandcamp label URL.' 
      };
    }
    
    // Normalize the URL to prevent duplicates
    let normalizedUrl = labelUrl.toLowerCase().trim().replace(/\/+$/, '');
    
    // Extract the root domain for label URLs
    try {
      const url = new URL(normalizedUrl);
      
      // If it's a subdomain.bandcamp.com URL, normalize to just the root domain
      if (url.hostname.endsWith('.bandcamp.com') && url.hostname !== 'bandcamp.com') {
        // Keep only the hostname part for subdomain URLs (e.g., allmythoughts.bandcamp.com)
        normalizedUrl = `${url.protocol}//${url.hostname}`;
      } else if (url.hostname === 'bandcamp.com' && url.pathname.startsWith('/label/')) {
        // For bandcamp.com/label/xxx URLs, keep only the /label/xxx part
        const labelPathMatch = url.pathname.match(new RegExp('^/label/([^/]+)'));
        if (labelPathMatch) {
          normalizedUrl = `${url.protocol}//${url.hostname}/label/${labelPathMatch[1]}`;
        }
      }
    } catch (error) {
      console.error('Error parsing URL:', error);
      // If URL parsing fails, continue with the basic normalization
    }
    
    // Enhanced validation for Bandcamp label URLs
    // Valid formats:
    // 1. https://[label-name].bandcamp.com
    // 2. https://bandcamp.com/label/[label-name]
    
    const labelDomainPattern = new RegExp('^https?://[a-z0-9-]+\\.bandcamp\\.com/?$', 'i');
    const labelPathPattern = new RegExp('^https?://bandcamp\\.com/label/[a-z0-9-]+/?$', 'i');
    
    if (!labelDomainPattern.test(normalizedUrl) && !labelPathPattern.test(normalizedUrl)) {
      return {
        success: false,
        error: 'The URL does not appear to be a valid Bandcamp label URL. Please provide a URL like "https://balmat.bandcamp.com".'
      };
    }
    
    // Check if this URL has already been submitted
    const { data: existingLabels, error: checkError } = await supabase
      .from('labels_to_add')
      .select('id')
      .eq('label_url', normalizedUrl)
      .limit(1);
      
    if (checkError) {
      console.error('Error checking for existing label:', checkError);
      throw checkError;
    }
    
    // If the URL already exists, return success but inform the caller
    if (existingLabels && existingLabels.length > 0) {
      return { 
        success: true,
        error: 'This label is already in the queue and is awaiting processing and validation. New submissions are processed every Friday.',
        labelId: existingLabels[0].id
      };
    }
    
    // Insert the new label URL into the labels_to_add table
    const { data, error: insertError } = await supabase
      .from('labels_to_add')
      .insert([
        { 
          label_url: normalizedUrl,
          is_processed: false,
          is_valid: false,
          user_id: userId
        }
      ])
      .select('id');
    
    if (insertError) {
      console.error('Error inserting label URL:', insertError);
      throw insertError;
    }
    
    console.log(`User ${userId} submitted label URL: ${normalizedUrl}`);
    
    // Return success with the new label ID if available
    return { 
      success: true,
      labelId: data && data.length > 0 ? data[0].id : undefined
    };
    
  } catch (error) {
    console.error('Error submitting label URL:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

/**
 * Fetches labels with their listen status (fully checked or not)
 * @param filterType - 'checked', 'unchecked', or 'all'
 * @param page - Page number for pagination
 * @param itemsPerPage - Number of items per page
 * @param searchTerm - Optional search term to filter labels
 * @param countOnly - If true, only returns count without fetching data
 */
export const fetchLabelsByListenStatus = async (
  filterType: 'checked' | 'unchecked' | 'all' = 'all',
  page: number = 1, 
  itemsPerPage: number = 20, 
  searchTerm: string | null = null,
  countOnly: boolean = false
): Promise<{ data: Label[], count: number }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('No authenticated user found in fetchLabelsByListenStatus');
      return { data: [], count: 0 };
    }

    // Get the total count
    const { data: countData, error: countError } = await supabase
      .rpc('count_filtered_labels_by_listen_status', {
        user_id_param: user.id,
        filter_type: filterType,
        search_term: searchTerm
      });

    if (countError) {
      console.error('Error counting labels by listen status:', countError);
      throw countError;
    }

    // If countOnly is true, return early with just the count
    if (countOnly) {
      return { data: [], count: countData || 0 };
    }

    // Fetch labels using our database function
    const { data, error } = await supabase
      .rpc('get_filtered_labels_by_listen_status', { 
        user_id_param: user.id,
        filter_type: filterType,
        page_number: page,
        items_per_page: itemsPerPage,
        search_term: searchTerm
      });

    if (error) {
      console.error('Error fetching labels by listen status:', error);
      throw error;
    }

    // Convert data to Label type
    const labels: Label[] = data.map((item: any) => ({
      id: item.id,
      label_name: item.label_name,
      label_url: item.label_url,
      tags: item.tags,
      is_fully_checked: item.is_fully_checked,
      releases: [], // Empty initially
    }));

    return { 
      data: labels, 
      count: countData || 0 
    };
  } catch (error) {
    console.error('Error in fetchLabelsByListenStatus:', error);
    return { data: [], count: 0 };
  }
};

/**
 * Fetches ALL labels with their listen status (fully checked or not) without pagination
 * @param filterType - 'checked', 'unchecked', or 'all'
 */
export const fetchAllLabelsByListenStatus = async (
  filterType: 'checked' | 'unchecked' | 'all' = 'all'
): Promise<Label[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('No authenticated user found in fetchAllLabelsByListenStatus');
      return [];
    }

    console.log('Attempting to call get_all_filtered_labels_by_listen_status with params:', {
      user_id_param: user.id,
      filter_type: filterType
    });

    // Fetch all labels without pagination using our database function
    const { data, error } = await supabase
      .rpc('get_all_filtered_labels_by_listen_status', { 
        user_id_param: user.id,
        filter_type: filterType
      });

    if (error) {
      console.error('Error fetching all labels by listen status:', error);
      // Try fallback to paginated function if all fetch fails
      console.log('Trying fallback to paginated function...');
      return await fallbackToLabelsByListenStatus(filterType, user.id);
    }

    // Convert data to Label type
    const labels: Label[] = data.map((item: any) => ({
      id: item.id,
      label_name: item.label_name,
      label_url: item.label_url,
      tags: item.tags,
      is_fully_checked: item.is_fully_checked,
      releases: [], // Empty initially
    }));

    return labels;
  } catch (error) {
    console.error('Error in fetchAllLabelsByListenStatus:', error);
    // Fallback to the existing function that works
    console.log('Error caught, falling back to existing function');
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      return await fallbackToLabelsByListenStatus(filterType, user.id);
    }
    return [];
  }
};

// Fallback function that uses the existing paginated function
async function fallbackToLabelsByListenStatus(
  filterType: 'checked' | 'unchecked' | 'all',
  userId: string
): Promise<Label[]> {
  try {
    console.log('Running fallback function with existing paginated API');
    // Get a large page size to effectively get "all" labels
    const { data } = await fetchLabelsByListenStatus(filterType, 1, 500);
    return data;
  } catch (fallbackError) {
    console.error('Even fallback function failed:', fallbackError);
    return [];
  }
} 